<script setup lang="ts">
import { TRIANGLE_HEIGHT, TRIANGLE_WIDTH } from '@/constants/tooltip-dialog.constants';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import TooltipTriangle from '@/core/tooltip/TooltipTriangle.vue';
import { clampNumber } from 'common/utils/math.utils';
import { EPosition } from 'common/enum/misc.enum';
import { ref, toRefs, useTemplateRef, watch } from 'vue';

const emit = defineEmits<{
  hide: [];
  'mouseleave:content': [];
  'mouseenter:content': [];
  'click:outside': [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement?: HTMLElement | null;
    position?: EPosition;
    theme?: 'tint' | 'info';
    flipStrategy?: 'both' | 'horizontal' | 'vertical';
    contentStyle?: string;
    popoverContentStyle?: string;
    disableClamping?: boolean;
    showArrow?: boolean;
    offsetY?: number;
    offsetX?: number;
    persistent?: boolean;
  }>(),
  {
    anchorElement: null,
    contentStyle: '',
    popoverContentStyle: '',
    disableClamping: false,
    flipStrategy: 'both',
    offsetX: 0,
    offsetY: 0,
    position: EPosition.RIGHT,
    theme: 'tint',
    persistent: false,
    showArrow: true,
  },
);
const { position, anchorElement } = toRefs(props);

const dialogRef = useTemplateRef<InstanceType<typeof PositionablePopover>>('dialogRef');
const triangleStyle = ref<Record<string, string>>({});
const positionInternal = ref<EPosition>(EPosition.RIGHT);
const TRIANGLE_VIEWPORT_OFFSET = 2;

const getHorizontalTriangleLeft = (anchorRect: DOMRect, dialogRect: DOMRect): number => {
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  return clampNumber(
    anchorCenterX - dialogRect.left - TRIANGLE_WIDTH / 2,
    0,
    Math.max(dialogRect.width - TRIANGLE_WIDTH, 0),
  );
};

const getVerticalTriangleTop = (anchorRect: DOMRect, dialogRect: DOMRect): number => {
  const anchorCenterY = anchorRect.top + anchorRect.height / 2;
  return clampNumber(
    anchorCenterY - dialogRect.top - TRIANGLE_HEIGHT / 2,
    0,
    Math.max(dialogRect.height - TRIANGLE_HEIGHT, 0),
  );
};

const updateTriangleStyle = (): void => {
  const anchorRect = anchorElement.value?.getBoundingClientRect();
  const dialogRect = dialogRef.value?.getDialogElement()?.getBoundingClientRect();
  let style: Record<string, string> = {};

  if (!dialogRect || !anchorRect) return;
  switch (positionInternal.value) {
    case EPosition.BOTTOM_RIGHT:
    case EPosition.BOTTOM_LEFT:
    case EPosition.BOTTOM_CENTER:
      style = {
        top: `${TRIANGLE_VIEWPORT_OFFSET}px`,
        left: `${Math.round(getHorizontalTriangleLeft(anchorRect, dialogRect))}px`,
        transform: 'rotate(180deg)',
      };
      break;

    case EPosition.LEFT:
      style = {
        top: `${Math.round(getVerticalTriangleTop(anchorRect, dialogRect))}px`,
        right: `${(TRIANGLE_HEIGHT / 2) * -1 + TRIANGLE_VIEWPORT_OFFSET}px`,
        transform: 'rotate(-90deg)',
        transformOrigin: 'center',
      };
      break;

    case EPosition.TOP_LEFT:
    case EPosition.TOP_RIGHT:
    case EPosition.TOP_CENTER:
      style = {
        bottom: `${TRIANGLE_VIEWPORT_OFFSET}px`,
        left: `${Math.round(getHorizontalTriangleLeft(anchorRect, dialogRect))}px`,
      };
      break;

    case EPosition.RIGHT:
    default:
      style = {
        top: `${Math.round(getVerticalTriangleTop(anchorRect, dialogRect))}px`,
        left: `${(TRIANGLE_HEIGHT / 2) * -1 + TRIANGLE_VIEWPORT_OFFSET}px`,
        transform: 'rotate(90deg)',
      };
  }
  triangleStyle.value = style;
};

const hide = (): void => {
  emit('hide');
  dialogRef.value?.hide();
};

const show = (): void => {
  const dialog = dialogRef.value;
  if (!dialog) return;

  void dialog.show().then(updateTriangleStyle);
};

defineExpose({
  show,
  hide,
});

watch(position, () => (positionInternal.value = position.value), { immediate: true });
watch([anchorElement, positionInternal], updateTriangleStyle, { immediate: true });
</script>

<template>
  <PositionablePopover
    v-if="anchorElement"
    ref="dialogRef"
    :anchor-element="anchorElement"
    :dialog-style="['!bg-transparent', !showArrow && '!overflow-visible'].filter(Boolean).join(' ')"
    :shadow="false"
    :position="position"
    :flip-strategy="flipStrategy"
    :disable-clamping="disableClamping"
    :disable-click-outside="props.persistent"
    :content-style="popoverContentStyle"
    :offset-y="offsetY"
    :offset-x="offsetX"
    @update:position="positionInternal = $event"
    @positioned="updateTriangleStyle"
    @hide="emit('hide')"
    @mouseenter:content="emit('mouseenter:content')"
    @mouseleave:content="emit('mouseleave:content')"
    @click:outside="emit('click:outside')"
  >
    <template #above-content>
      <TooltipTriangle
        v-if="showArrow"
        :style="triangleStyle"
        :class="['absolute', theme === 'info' ? 'text-info-strong' : 'text-tint']"
      />
    </template>
    <div
      :class="[
        positionInternal === EPosition.RIGHT && 'ml-md',
        positionInternal === EPosition.BOTTOM_LEFT && 'mt-md',
        positionInternal === EPosition.BOTTOM_RIGHT && 'mt-md',
        positionInternal === EPosition.LEFT && 'mr-md',
        positionInternal === EPosition.BOTTOM_CENTER && 'mt-md',
        positionInternal === EPosition.TOP_CENTER && 'mb-md',
        positionInternal === EPosition.TOP_LEFT && 'mb-md',
        positionInternal === EPosition.TOP_RIGHT && 'mb-md',
        'flex flex-col',
        'rounded-group overflow-hidden',
        'text-on-tint',
        theme === 'info' ? 'bg-info-strong' : 'bg-tint',
        'p-lg',
        contentStyle,
      ]"
      @click="!props.persistent && hide()"
    >
      <slot name="content" />
    </div>
  </PositionablePopover>
</template>
